import type {
  CSVParseConfig,
  CSVParseResult,
  ImportError,
  ImportResult,
} from '../types';
import { type CsvDelimiter, parseCSVLine } from './csvLineParser';
import {
  AmbiguousCsvDelimiterError,
  detectCsvDelimiter,
} from './detectCsvDelimiter';

export const validateAndParseCSV = async <T>(
  file: File | null,
  config: CSVParseConfig<T>,
): Promise<CSVParseResult<T>> => {
  if (!file) {
    return {
      isValid: false,
      error: 'No file provided',
    };
  }

  try {
    const content = await readFileContent(file);

    if (!content.trim()) {
      return {
        isValid: false,
        error: 'The uploaded file is empty.',
      };
    }

    const result = await parseCSVContent(content, config);
    return {
      isValid: true,
      result,
    };
  } catch (error) {
    return {
      isValid: false,
      error:
        error instanceof Error
          ? error.message
          : 'An error occurred while reading the file.',
    };
  }
};

const readFileContent = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      resolve(content);
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsText(file);
  });
};

const parseCSVContent = async <T>(
  content: string,
  config: CSVParseConfig<T>,
): Promise<ImportResult<T>> => {
  const lines = content
    .split(/\r?\n/) // split lines (LF or CRLF)
    .filter(Boolean) // drop empty lines
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length < 2) {
    return {
      validData: [],
      errors: [
        {
          id: 'File Error',
          message: 'CSV file must contain headers and at least one data row',
        },
      ],
    };
  }

  const expectedColumnCount = config.headers.length;
  let delimiter: CsvDelimiter;
  try {
    delimiter = detectCsvDelimiter(lines, expectedColumnCount);
  } catch (error) {
    if (error instanceof AmbiguousCsvDelimiterError) {
      return {
        validData: [],
        errors: [
          {
            id: 'File Error',
            message: error.message,
          },
        ],
      };
    }
    return {
      validData: [],
      errors: [
        {
          id: 'File Error',
          message:
            error instanceof Error
              ? error.message
              : 'Could not detect CSV delimiter',
        },
      ],
    };
  }

  const headerLine = lines[0]!;
  const actualHeaders = parseCSVLine(headerLine, delimiter);
  const expectedHeaders = Array.from(config.headers);
  const missingHeaders = expectedHeaders.filter(
    (header) => !actualHeaders.includes(header),
  );

  if (missingHeaders.length > 0) {
    return {
      validData: [],
      errors: [
        {
          id: 'Line 0',
          message: `Missing required headers: ${missingHeaders.join(', ')}`,
        },
      ],
    };
  }

  // Parse data rows
  const validData: T[] = [];
  const errors: ImportError[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]!;
    const rowIndex = i + 1; // +1 because we're counting from header

    try {
      const rowData = await parseDataRow(
        line,
        actualHeaders,
        config,
        rowIndex,
        delimiter,
      );
      if (rowData) {
        validData.push(rowData);
      }
    } catch (error) {
      errors.push({
        id: `Line ${rowIndex}`,
        message: error instanceof Error ? error.message : 'Invalid row format',
      });
    }
  }

  return { validData, errors };
};

const parseDataRow = async <T>(
  line: string,
  headers: string[],
  config: CSVParseConfig<T>,
  rowIndex: number,
  delimiter: CsvDelimiter,
): Promise<T | null> => {
  const values = parseCSVLine(line, delimiter);

  if (values.length !== headers.length) {
    throw new Error(
      `Expected ${headers.length} columns, but found ${values.length}`,
    );
  }

  const row: Record<string, string> = {};
  headers.forEach((header, headerIndex) => {
    row[header] = values[headerIndex] ?? '';
  });

  const validatedRow = validateRowData(config, row, delimiter);

  return config.rowTransformer(validatedRow, rowIndex);
};

const validateRowData = <T>(
  config: CSVParseConfig<T>,
  row: Record<string, string>,
  csvDelimiter: CsvDelimiter,
): Record<string, unknown> => {
  const validatedRow: Record<string, unknown> = {};
  const rowErrors: string[] = [];

  for (const rule of config.rules) {
    const value = row[rule.field] || '';

    if (rule.required && !value) {
      rowErrors.push(`Missing ${rule.field}`);
      continue;
    }

    if (rule.validator) {
      const validationError = rule.validator(value, row);
      if (validationError) {
        rowErrors.push(validationError);
        continue;
      }
    }

    if (rule.transformer) {
      validatedRow[rule.field] = rule.transformer(value, row, csvDelimiter);
    } else {
      validatedRow[rule.field] = value;
    }
  }

  if (rowErrors.length > 0) {
    throw new Error(rowErrors.join(', '));
  }

  return validatedRow;
};
